import { useState, useRef, useEffect, useCallback } from 'react';
import { CALC_TYPE, FORM_NAMES } from '~/app/constants';
import ErrorHelpers from '~/app/errorHelpers.js';

// Delay before focusing a verify field (ms)
const FieldFocusDelay = 100;

// Regex: asset form names start with 'A' (e.g. 'A00001')
const assetFormRegExp = /^[Aa]/;

/**
 * useVerify
 *
 * Owns the verify / error-reject flow for FormViewer:
 *
 *   State: verifyDirty, verifyField, verifyForm, verifyStmCode, verifyWksRan,
 *          verifyType, activeErrorRejectTab (local component state).
 *
 *   Methods exported for FormViewer:
 *     verifyReturn(tab)                            ← was navCallbacksRef.onVerifyReturn
 *     handleVerify(form, stmCode, file, field, …)  ← passed to ErrorRejectModal
 *     verifyFocus(field, tab)                      ← internal; also exported for reuse
 *     loadVerifyForm(currentFormName, currentFileName) ← was navCallbacksRef.onLoadVerifyForm
 *
 *   Dual state+ref: verifyDirty state syncs into verifyDirtyRef so that the
 *   useFormNavigation event handlers can read it synchronously.
 *
 * Inputs:
 *   calcAPIRef           – ref to the CalcAPI instance (from useCalcSession)
 *   verifyDirtyRef       – shared ref; this hook keeps .current in sync with verifyDirty state
 *   formNavRef           – synchronously-current ref to useFormNavigation result
 *   onStartSpin          – class.startSpin
 *   onShowErrorSnackbar  – class.showErrorSnackbar
 *   onCloseErrorRejectModal – props.closeErrorRejectModal
 *   onViewAsset          – class.viewAsset (async, for asset-form verify)
 *   onPerformCalcProcess – class.performCalcProcess (called inside verifyFocus change handler)
 *   onUnbindFormEvents   – fieldEvents.unbindFormEvents
 *   onBindFormEvents     – fieldEvents.bindFormEvents
 */
const useVerify = ({
  calcAPIRef,
  verifyDirtyRef,
  formNavRef,
  onStartSpin,
  onShowErrorSnackbar,
  onCloseErrorRejectModal,
  onViewAsset,
  onPerformCalcProcess,
  onUnbindFormEvents,
  onBindFormEvents,
}) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [verifyDirty, setVerifyDirtyState] = useState(false);
  const [verifyField, setVerifyField] = useState('');
  const [verifyForm, setVerifyForm] = useState('');
  const [verifyStmCode, setVerifyStmCode] = useState('');
  const [verifyWksRan, setVerifyWksRan] = useState(false);
  const [verifyType, setVerifyType] = useState('');
  const [activeErrorRejectTab, setActiveErrorRejectTab] = useState(0);

  // ── Sync verifyDirty → verifyDirtyRef ─────────────────────────────────────
  const setVerifyDirty = useCallback(
    val => {
      setVerifyDirtyState(val);
      if (verifyDirtyRef) verifyDirtyRef.current = val;
    },
    [verifyDirtyRef],
  );

  // ── Stable internal-state refs (read by callbacks that close over initial render) ──
  const verifyFieldRef = useRef('');
  const verifyFormRef = useRef('');
  const verifyStmCodeRef = useRef('');
  const verifyWksRanRef = useRef(false);
  const activeErrorRejectTabRef = useRef(0);

  useEffect(() => {
    verifyFieldRef.current = verifyField;
  }, [verifyField]);
  useEffect(() => {
    verifyFormRef.current = verifyForm;
  }, [verifyForm]);
  useEffect(() => {
    verifyStmCodeRef.current = verifyStmCode;
  }, [verifyStmCode]);
  useEffect(() => {
    verifyWksRanRef.current = verifyWksRan;
  }, [verifyWksRan]);
  useEffect(() => {
    activeErrorRejectTabRef.current = activeErrorRejectTab;
  }, [activeErrorRejectTab]);

  // ── Callback refs — always current, no stale-closure issues ───────────────
  const onStartSpinRef = useRef(onStartSpin);
  const onShowErrorSnackbarRef = useRef(onShowErrorSnackbar);
  const onCloseErrorRejectModalRef = useRef(onCloseErrorRejectModal);
  const onViewAssetRef = useRef(onViewAsset);
  const onPerformCalcProcessRef = useRef(onPerformCalcProcess);
  const onUnbindFormEventsRef = useRef(onUnbindFormEvents);
  const onBindFormEventsRef = useRef(onBindFormEvents);

  useEffect(() => {
    onStartSpinRef.current = onStartSpin;
    onShowErrorSnackbarRef.current = onShowErrorSnackbar;
    onCloseErrorRejectModalRef.current = onCloseErrorRejectModal;
    onViewAssetRef.current = onViewAsset;
    onPerformCalcProcessRef.current = onPerformCalcProcess;
    onUnbindFormEventsRef.current = onUnbindFormEvents;
    onBindFormEventsRef.current = onBindFormEvents;
  });

  // ── Instance var ──────────────────────────────────────────────────────────
  const verifyFormLoadCountRef = useRef(0);

  // ── verifyReturn ──────────────────────────────────────────────────────────

  /**
   * Triggers ReturnVerify on the calc server and opens the error/reject modal.
   * Replaces navCallbacksRef.onVerifyReturn.
   */
  const verifyReturn = useCallback(
    async activeTab => {
      try {
        onStartSpinRef.current?.('Checking for errors...');
        await calcAPIRef.current.ReturnVerify();
        setActiveErrorRejectTab(activeTab);
      } catch (error) {
        onShowErrorSnackbarRef.current?.('Error Verifying Return: ', error);
      }
    },
    [calcAPIRef],
  );

  // ── verifyFocus ───────────────────────────────────────────────────────────

  /**
   * Focuses the verify field and applies a red border change handler.
   */
  const verifyFocus = useCallback(
    (field, tab) => {
      setTimeout(() => {
        let fieldExistCount = 0;
        const fieldExists = setInterval(() => {
          if (document.getElementById(field) || fieldExistCount >= 10) {
            // RMST00 / RMST01 CLO workaround
            if (
              field === 'RMST00' &&
              document.getElementById(field)?.classList.contains('hide') &&
              document.getElementById('RMST01') &&
              !document.getElementById('RMST01').classList.contains('hide')
            ) {
              field = 'RMST01';
            }

            const el = document.getElementById(field);
            el?.focus();

            if (el) {
              const initBorder = el.style.border;
              el.style.border = '2px solid red';
              const changeHandler = e => {
                e.preventDefault();
                const activeForm = formNavRef.current?.activeForm ?? '';
                onPerformCalcProcessRef.current?.(
                  activeForm,
                  assetFormRegExp.test(activeForm) ? 'X' : '',
                  e.target.id,
                  e.target.value,
                  CALC_TYPE.FIELD_CALC,
                  tab,
                );
                el.style.border = initBorder;
                el.removeEventListener('change', changeHandler);
                onUnbindFormEventsRef.current?.();
                onBindFormEventsRef.current?.();
              };
              el.addEventListener('change', changeHandler);
            }

            clearInterval(fieldExists);
          }
          fieldExistCount++;
        }, FieldFocusDelay);
      }, FieldFocusDelay);
    },
    [formNavRef],
  );

  // resetVerifyNavigation() only clears the navigation/chase state used to
  // locate and focus the verify target field. It does not directly clear
  // verifyDirty; verifyDirty may still be cleared later if the verify chase is
  // considered abandoned (see resetVerify() in loadVerifyForm()).
  const resetVerifyNavigation = useCallback(() => {
    verifyFormLoadCountRef.current = 0;
    setVerifyStmCode('');
    setVerifyForm('');
    setVerifyWksRan(false);
  }, []);

  // ── handleVerify ──────────────────────────────────────────────────────────

  /**
   * Called from ErrorRejectModal when the user clicks a verify item.
   * Loads the relevant form (if needed) and focuses the field.
   */
  const handleVerify = useCallback(
    async (form, stmCode, file, field, desc, tab) => {
      onCloseErrorRejectModalRef.current?.();

      if (form === '000000') form = FORM_NAMES.CDS;

      if (!stmCode) stmCode = '';

      setVerifyWksRan(false);
      setVerifyDirty(true);
      setVerifyField(field);
      setActiveErrorRejectTab(tab);
      setVerifyForm(form + stmCode);
      setVerifyStmCode(stmCode);

      const activeForm = formNavRef.current?.activeForm ?? '';

      // Parent first (mirrors desktop CMDMOVE -> MOVEFIND). If the field is already
      // on the displayed form (the parent OR its worksheet) just focus it, no reload.
      if (
        (activeForm === form || activeForm === form + stmCode) &&
        document.getElementById(field)
      ) {
        verifyFocus(field, tab);

        resetVerifyNavigation();
      } else {
        const formName = form;

        if (assetFormRegExp.test(formName)) {
          try {
            await onViewAssetRef.current?.(formName);
          } catch (error) {
            ErrorHelpers.handleError('Error loading Form', error);
            console.log('formName: ', formName, 'error: ', error);
          }
        } else {
          // Load the PARENT form first and land on the field if it lives there.
          // loadVerifyForm() escalates to the worksheet/statement ('wks' -> 'stm')
          // only when the field is NOT found on the parent. see loadVerifyForm below.
          try {
            await formNavRef.current?.getTaxForm(formName, '', file, '');
          } catch (error) {
            ErrorHelpers.handleError('Error loading Form', error);
            console.log('formName: ', formName, 'error: ', error);
          }
        }
      }
    },
    [formNavRef, setVerifyDirty, verifyFocus, resetVerifyNavigation],
  );

  // ── resetVerify ───────────────────────────────────────────────────────────

  /**
   * Aborts/resets the verify flow: clears the verify state and the verify-field
   * chase counter so the next verify cycle starts clean. Called when a form
   * loads successfully in verify mode, by return_update, and when the chase is
   * abandoned (manual navigation / non-target form load).
   *
   * @param {object} [override] – optional individual field overrides
   */
  const resetVerify = useCallback(
    ({
      verifyDirty: d = false,
      verifyStmCode: sc = '',
      verifyForm: vf = '',
      verifyWksRan: hasVerifyWksRan = false,
    } = {}) => {
      verifyFormLoadCountRef.current = 0;
      setVerifyDirty(d);
      setVerifyStmCode(sc);
      setVerifyForm(vf);
      setVerifyWksRan(hasVerifyWksRan);
    },
    [setVerifyDirty],
  );

  // ── loadVerifyForm ────────────────────────────────────────────────────────

  /**
   * Called after each form_load while in verify mode to try to focus the verify field.
   * Replaces navCallbacksRef.onLoadVerifyForm.
   */

  const loadVerifyForm = useCallback(
    (currentFormName, currentFileName) => {
      if (currentFormName.length > 6) {
        currentFormName = currentFormName.slice(0, 6);
      }

      const stmCode = verifyStmCodeRef.current;
      let formToLoad = currentFormName + stmCode;

      if (verifyFormLoadCountRef.current < 3) {
        const field = verifyFieldRef.current;
        const vForm = verifyFormRef.current;
        const wksRan = verifyWksRanRef.current;
        const tab = activeErrorRejectTabRef.current;

        if (document.getElementById(field)) {
          verifyFormLoadCountRef.current = 0;
          setVerifyWksRan(false);
          verifyFocus(field, tab);

          resetVerifyNavigation();
        } else if (!wksRan && vForm === formToLoad) {
          if (stmCode === '') {
            formToLoad = formToLoad + field.slice(0, 1);
          }
          setVerifyWksRan(true);
          verifyFormLoadCountRef.current++;
          formNavRef.current?.getTaxForm(formToLoad, 'wks', currentFileName, field);
        } else if (wksRan && vForm === formToLoad) {
          setVerifyWksRan(false);
          if (stmCode === '') {
            formToLoad = formToLoad + field.slice(0, 1);
          }
          verifyFormLoadCountRef.current++;
          formNavRef.current?.getTaxForm(formToLoad, 'stm', field);
        } else {
          // The just-loaded form is not the verify target — the chase was
          // abandoned (e.g. the user navigated to an unrelated form). Clear
          // chase state so we don't issue a stale form_load against it.
          // resetVerify() also zeroes verifyFormLoadCountRef.
          resetVerify();
        }
      } else {
        verifyFormLoadCountRef.current = 0;
      }
    },
    [formNavRef, verifyFocus, resetVerify, resetVerifyNavigation],
  );

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // State for rendering / external reads
    verifyDirty,
    verifyField,
    verifyForm,
    verifyStmCode,
    verifyWksRan,
    verifyType,
    activeErrorRejectTab,

    // State setters needed by FormViewer class
    setVerifyDirty,
    setVerifyType,
    setActiveErrorRejectTab,
    setVerifyWksRan,
    resetVerify,

    // Methods registered in navCallbacksRef / passed to ErrorRejectModal
    verifyReturn,
    handleVerify,
    verifyFocus,
    loadVerifyForm,
  };
};

export default useVerify;
